<!-- views/ads/adsIndexV2.vue - 批量创建广告页面 - 优化版 -->
<template>
  <div class="batch-ad-creator">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-left">
        <h2>批量创建广告</h2>
        <p class="page-desc">灵活批量创建 - 支持多广告主、多广告组、多广告一站式配置</p>
      </div>
      <div class="header-actions">
        <el-dropdown @command="handleConfigCommand" style="margin-right: 8px;">
          <el-button type="primary" size="medium" icon="el-icon-s-tools">
            配置管理<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="save" icon="el-icon-document-add">保存当前配置</el-dropdown-item>
            <el-dropdown-item command="load" icon="el-icon-document-copy">加载历史配置</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <el-button type="success" size="medium" icon="el-icon-check" @click="handleBatchSubmit" :loading="submitLoading" :disabled="!canSubmit">
          批量创建 ({{ totalAdsCount }}个广告)
        </el-button>
        <el-button size="medium" icon="el-icon-refresh" @click="resetAll">
          重置
        </el-button>
      </div>
    </div>

    <el-card v-if="submitLoading" shadow="never" class="submit-progress-card">
      <div class="submit-progress-header">
        <div>
          <span class="submit-progress-title">正在批量创建广告</span>
          <span class="submit-progress-meta">批次 {{ submitProgress.batchId }}，并发 {{ submitProgress.concurrency }}</span>
        </div>
        <div class="submit-progress-count">
          {{ submitProgress.completed }}/{{ submitProgress.total }}
        </div>
      </div>
      <el-progress
          :percentage="submitProgressPercent"
          :stroke-width="10"
          :format="() => `${submitProgress.success} 成功 / ${submitProgress.fail} 失败`"
      />
    </el-card>

    <!-- 上半部分：三栏配置 -->
    <div class="config-layout">
      <!-- 左侧：视频素材 -->
      <div class="left-panel">
        <el-card shadow="never" class="config-card">
          <div slot="header" class="card-header">
            <span><i class="el-icon-video-camera"></i> 视频素材库</span>
            <div class="header-actions">
              <el-button type="primary" size="mini" icon="el-icon-search" @click="showServerMaterials">
                添加素材
              </el-button>
              <el-button size="mini" icon="el-icon-sort" @click="shuffleGlobalVideoLibrary" :disabled="globalVideoLibrary.length < 2">
                打乱顺序
              </el-button>
              <el-button size="mini" icon="el-icon-refresh" @click="refreshGlobalMaterialReview" :loading="materialReviewLoading" :disabled="globalVideoLibrary.length === 0">
                刷新审核
              </el-button>
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="deleteRejectedReviewedVideos" :loading="deleteRejectedLoading" :disabled="rejectedReviewedVideos.length === 0">
                删除不过审
              </el-button>
              <el-button size="mini" icon="el-icon-delete" @click="clearGlobalLibrary" :disabled="globalVideoLibrary.length === 0">
                清空
              </el-button>
            </div>
          </div>

          <!-- 素材统计 -->
          <div class="materials-stats">
            <el-tag type="info" size="small">总数: {{ globalVideoLibrary.length }}</el-tag>
            <el-tag type="success" size="small">已选: {{ selectedVideos.length }}</el-tag>
            <el-tag type="success" size="small">通过: {{ materialReviewStats.passed }}</el-tag>
            <el-tag type="danger" size="small">失败: {{ materialReviewStats.rejected }}</el-tag>
            <el-tag type="warning" size="small">审核中: {{ materialReviewStats.pending }}</el-tag>
            <el-tag type="info" size="small">未投放: {{ materialReviewStats.noAdMaterial }}</el-tag>
          </div>

          <!-- 视频网格 - 限制3行，超出滚动 -->
          <div v-if="globalVideoLibrary.length > 0" class="video-grid">
            <div
                v-for="(video, index) in globalVideoLibrary"
                :key="index"
                class="video-item"
                :class="{ selected: video.selected }"
                @click="toggleVideoSelection(index)"
                @mouseenter="handleVideoMouseEnter(index)"
                @mouseleave="handleVideoMouseLeave"
            >
              <div class="video-checkbox">
                <el-checkbox v-model="video.selected" @click.stop></el-checkbox>
              </div>
              <div class="video-preview-wrapper">
                <!-- 临时测试：如果img不显示，可以先用video测试 -->
                <!-- <video :src="getVideoUrl(video)" controls preload="none" muted></video> -->
                
                <!-- 使用img替代video标签,只在hover或选中时加载video -->
                <img 
                  v-if="!isVideoHovered(index)"
                  :src="getVideoThumbnail(video)" 
                  alt="视频封面"
                  class="video-thumbnail"
                  @error="handleImageError($event, video)"
                />
                <video 
                  v-else
                  :src="getVideoUrl(video)" 
                  controls
                  preload="none"
                  muted
                ></video>
              </div>
              <div class="video-info">
                <span class="video-index">#{{ index + 1 }}</span>
                <el-tooltip :content="getVideoFileName(video)" placement="top">
                  <span class="video-name">{{ getVideoFileName(video) }}</span>
                </el-tooltip>
                <div class="video-review-line">
                  <el-tag :type="getVideoReviewTagType(video)" size="mini">
                    {{ getVideoReviewText(video) }}
                  </el-tag>
                </div>
              </div>
            </div>
          </div>

          <el-empty v-else description="暂无视频素材，请点击上方按钮添加">
            <el-button type="primary" size="small" @click="showServerMaterials">添加素材</el-button>
          </el-empty>

          <!-- 批量操作 -->
          <div v-if="globalVideoLibrary.length > 0" class="batch-actions">
            <el-button size="small" @click="selectAllVideos">
              {{ isAllSelected ? '取消全选' : '全选' }}
            </el-button>
            <el-button size="small" type="danger" @click="removeSelectedVideos" :disabled="selectedVideos.length === 0">
              删除选中 ({{ selectedVideos.length }})
            </el-button>
          </div>
        </el-card>
      </div>

      <!-- 中间：广告主和广告组配置 -->
      <div class="center-panel">
        <el-card shadow="never" class="config-card">
          <div slot="header" class="card-header">
            <span><i class="el-icon-s-custom"></i> 广告主与广告组</span>
            <el-button type="primary" size="mini" icon="el-icon-plus" @click="openCreateAdgroupDialog">
              新建广告组
            </el-button>
          </div>

          <!-- 广告主选择 -->
          <div class="config-section">
            <div class="section-title">选择广告主 <span class="required">*</span></div>
            <el-select
                v-model="selectedAdvertiserIds"
                placeholder="请选择广告主（可多选）"
                multiple
                collapse-tags
                filterable
                style="width: 100%"
                @change="handleAdvertisersChange"
            >
              <el-option
                  v-for="item in advertisers"
                  :key="item.advertiser_id"
                  :label="`${item.advertiser_name.replace('-SUNNY-SINO-TT','')} (${item.advertiser_id})`"
                  :value="item.advertiser_id"
              />
            </el-select>
          </div>

          <!-- 广告组配置 -->
          <div v-for="(config, advIndex) in advertiserConfigs" :key="config.advertiserId" class="advertiser-config-block">
            <div class="adv-header">
              <span class="adv-name">{{ getAdvertiserName(config.advertiserId) }}</span>
              <el-button type="text" size="mini" icon="el-icon-close" @click="removeAdvertiser(advIndex)"></el-button>
            </div>

            <div class="adgroup-section">
              <div class="section-subtitle">广告组配置</div>
              <el-select
                  v-model="config.adgroupIds"
                  placeholder="选择广告组（可多选）"
                  multiple
                  collapse-tags
                  filterable
                  style="width: 100%"
                  :loading="adgroupLoading"
              >
                <el-option
                    v-for="item in config.availableAdgroups"
                    :key="item.adgroup_id"
                    :label="item.adgroup_name"
                    :value="item.adgroup_id"
                />
              </el-select>

              <div class="ad-count-config">
                <span class="label">每个广告组创建广告数：</span>
                <el-input-number
                    v-model="config.adsPerAdgroup"
                    :min="1"
                    :max="50"
                    :step="1"
                    size="small"
                />
              </div>
            </div>
          </div>

          <el-empty v-if="selectedAdvertiserIds.length === 0" description="请先选择广告主">
          </el-empty>
        </el-card>
      </div>

      <!-- 右侧：广告配置 -->
      <div class="right-panel">
        <el-card shadow="never" class="config-card">
          <div slot="header" class="card-header">
            <span><i class="el-icon-setting"></i> 广告配置</span>
          </div>

          <!-- 广告名称前缀 -->
          <div class="config-item">
            <div class="config-label">广告名称前缀</div>
            <el-input
                v-model="adConfig.adNamePrefix"
                placeholder="请输入广告名称前缀"
                size="small"
            />
            <div class="form-tip">格式: 前缀_广告主_广告组_序号</div>
          </div>

          <!-- 每广告视频数 -->
          <div class="config-item">
            <div class="config-label">每广告视频数 <span class="required">*</span></div>
            <el-input-number
                v-model="adConfig.videosPerAd"
                :min="1"
                :max="10"
                :step="1"
                size="small"
            />
            <el-button
                v-if="canAutoCalculate"
                type="text"
                size="mini"
                @click="autoCalculateVideosPerAd"
            >
              <i class="el-icon-magic-stick"></i> 自动计算
            </el-button>
          </div>

          <!-- 广告文案 -->
          <div class="config-item">
            <div class="config-label">广告文案</div>
            <div class="ad-text-list">
              <div
                  v-for="(text, index) in adConfig.adTexts"
                  :key="index"
                  class="ad-text-item"
              >
                <el-input
                    v-model="text.content"
                    placeholder="请输入广告文案"
                    maxlength="512"
                    show-word-limit
                    size="small"
                    style="flex: 1"
                />
                <el-button
                    type="text"
                    icon="el-icon-delete"
                    style="color: #f56c6c"
                    @click="removeAdText(index)"
                    v-if="adConfig.adTexts.length > 1"
                />
              </div>
              <el-button
                  type="dashed"
                  icon="el-icon-plus"
                  size="mini"
                  @click="addAdText"
                  v-if="adConfig.adTexts.length < 5"
              >
                添加文案
              </el-button>
            </div>
          </div>

          <!-- 推荐文案 -->
          <div class="config-item">
            <div class="config-label">推荐文案</div>
            <el-select
                v-model="adConfig.selectedCtas"
                multiple
                collapse-tags
                placeholder="请选择推荐文案（可选）"
                size="small"
                style="width: 100%"
                :loading="ctaLoading"
            >
              <el-option
                  v-for="item in ctaRecommendList"
                  :key="item.asset_content"
                  :label="item.asset_content"
                  :value="item.asset_content"
              />
            </el-select>
          </div>

          <!-- 广告发布者 -->
          <div class="config-item">
            <div class="config-label">广告发布者 <span class="required">*</span></div>
            <el-select
                v-model="adConfig.selectedIdentity"
                placeholder="请选择广告发布者"
                filterable
                size="small"
                style="width: 100%"
                :loading="identityLoading"
                @change="handleIdentityChange"
            >
              <el-option
                  v-for="item in identityList"
                  :key="item.identity_id"
                  :label="`${item.display_name} (${item.username})`"
                  :value="item.identity_id"
              >
                <div style="display: flex; align-items: center; gap: 8px">
                  <img v-if="item.profile_image" :src="item.profile_image" style="width: 24px; height: 24px; border-radius: 50%" />
                  <span>{{ item.display_name }} ({{ item.username }})</span>
                </div>
              </el-option>
            </el-select>
            <div class="form-tip">用于设置广告的发布身份</div>
          </div>

          <!-- 落地页 -->
          <div class="config-item">
            <div class="config-label">落地页URL</div>
            <el-input
                v-model="adConfig.landingPageUrl"
                placeholder="请输入落地页URL"
                size="small"
            />
          </div>

          <!-- 启用状态 -->
          <div class="config-item">
            <div class="config-label">启用状态</div>
            <el-switch
                v-model="adConfig.operationStatus"
                active-value="ENABLE"
                inactive-value="DISABLE"
                active-text="启用"
                inactive-text="禁用"
                size="small"
            />
          </div>
        </el-card>
      </div>
    </div>

    <!-- 下半部分：全宽预览面板 -->
    <el-card shadow="never" class="preview-section">
      <div slot="header" class="card-header">
        <span><i class="el-icon-view"></i> 创建预览</span>
        <el-tag :type="previewStatus.type" size="small">{{ previewStatus.text }}</el-tag>
      </div>

      <!-- 统计概览 -->
      <div class="preview-stats">
        <div class="stat-box">
          <div class="stat-value">{{ selectedVideos.length }}</div>
          <div class="stat-label">视频素材</div>
        </div>
        <div class="stat-box">
          <div class="stat-value">{{ selectedAdvertiserIds.length }}</div>
          <div class="stat-label">广告主</div>
        </div>
        <div class="stat-box">
          <div class="stat-value">{{ totalAdgroupsCount }}</div>
          <div class="stat-label">广告组</div>
        </div>
        <div class="stat-box highlight">
          <div class="stat-value">{{ totalAdsCount }}</div>
          <div class="stat-label">预计广告数</div>
        </div>
        <div class="stat-box" v-if="adConfig.videosPerAd > 0">
          <div class="stat-value">{{ adConfig.videosPerAd }}</div>
          <div class="stat-label">每广告视频</div>
        </div>
        <div class="stat-box info" v-if="totalAdsCount > 0">
          <div class="stat-value">{{ totalAdsCount * adConfig.videosPerAd }}</div>
          <div class="stat-label">需要视频数</div>
        </div>
      </div>

      <!-- 详细分配预览 -->
      <div v-if="adDistributionPreview.length > 0" class="distribution-preview">
        <div class="preview-title">广告分配详情</div>
        <el-table :data="flattenedPreviewData" size="small" border stripe max-height="400">
          <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
          <el-table-column prop="advertiserName" label="广告主" width="160" show-overflow-tooltip>

            <template slot-scope="{ row }">
              <div class="advertiser-name-cell" :title="row.advertiserName">{{ row.advertiserName.replace('-SUNNY-SINO-TT', '') }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="adgroupName" label="广告组" width="250" show-overflow-tooltip></el-table-column>
          <el-table-column prop="adName" label="广告名称" width="400" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <div class="ad-name-cell" :title="row.adName">{{ row.adName.replace('-SUNNY-SINO-TT', '')  }}</div>
            </template>
          </el-table-column>
          <el-table-column label="分配视频" width="400">
            <template slot-scope="{ row }">
              <div class="video-preview-cell">
                <!-- 预览按钮 -->
                <el-button
                    size="mini"
                    type="primary"
                    icon="el-icon-video-play"
                    @click="showVideoPreview(row)"
                >
                  预览 ({{ row.videoThumbnails ? row.videoThumbnails.length : 0 }})
                </el-button>
                <!-- 小预览图横向滚动 - 使用img替代video -->
                <div v-if="row.videoThumbnails && row.videoThumbnails.length > 0" class="video-thumbnails-scroll">
                  <div
                      v-for="(thumb, idx) in row.videoThumbnails"
                      :key="idx"
                      class="thumbnail-scroll-item"
                  >
                    <img :src="thumb" alt="视频缩略图" />
                    <span class="thumb-index">#{{ idx + 1 }}</span>
                  </div>
                </div>
                <!-- 视频信息 -->
                <div class="video-info-cell">
                  <el-tag size="mini" type="info">视频 {{ row.videoStartIndex + 1 }} - {{ row.videoEndIndex + 1 }}</el-tag>
                  <div class="video-names-cell" :title="row.videoNames">{{ row.videoNames }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <el-empty v-else description="配置信息不足，无法生成预览">
        <div class="empty-tip">请选择视频素材、广告主和广告组</div>
      </el-empty>
    </el-card>

    <!-- 新建广告组弹窗 -->
    <el-dialog
        title="批量创建广告组"
        :visible.sync="createAdgroupDialogVisible"
        width="700px"
        @close="handleCreateAdgroupDialogClose"
    >
      <el-alert
          title="为选中的广告主批量创建广告组（同时会创建对应的广告系列）"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom: 16px"
      />

      <el-form
          ref="createAdgroupForm"
          :model="createAdgroupFormData"
          :rules="createAdgroupFormRules"
          label-width="140px"
      >
        <el-form-item label="目标广告主" prop="targetAdvertisers">
          <el-select
              v-model="createAdgroupFormData.targetAdvertisers"
              placeholder="选择要创建广告组的广告主"
              multiple
              filterable
              style="width: 100%"
              @change="handleTargetAdvertisersChange"
          >
            <el-option
                v-for="item in advertisers"
                :key="item.advertiser_id"
                :label="`${item.advertiser_name} (${item.advertiser_id})`"
                :value="item.advertiser_id"
            />
          </el-select>
          <div class="form-tip">将同时为选中的每个广告主创建广告组</div>
        </el-form-item>

        <el-form-item label="每个广告主系列数" prop="campaignCount">
          <el-input-number
              v-model="createAdgroupFormData.campaignCount"
              :min="1"
              :max="50"
              :step="1"
              style="width: 200px"
          />
          <span class="form-tip">每个广告主将创建 {{ createAdgroupFormData.campaignCount }} 个广告系列</span>
        </el-form-item>

        <el-form-item label="每个系列广告组数" prop="adgroupCount">
          <el-input-number
              v-model="createAdgroupFormData.adgroupCount"
              :min="1"
              :max="50"
              :step="1"
              style="width: 200px"
          />
          <span class="form-tip">每个广告系列下将创建 {{ createAdgroupFormData.adgroupCount }} 个广告组</span>
        </el-form-item>

        <el-alert
            :closable="false"
            show-icon
            style="margin-bottom: 16px"
        >
          <template slot="title">
            <strong>创建预览：</strong>每个广告主将创建 <strong>{{ createAdgroupFormData.campaignCount }}</strong> 个广告系列，
            每个系列下创建 <strong>{{ createAdgroupFormData.adgroupCount }}</strong> 个广告组，
            共计 <strong>{{ createAdgroupFormData.campaignCount * createAdgroupFormData.adgroupCount }}</strong> 个广告组
          </template>
        </el-alert>

        <el-divider content-position="left">广告系列配置</el-divider>

        <el-form-item label="广告目标" prop="objective_type">
          <el-select v-model="createAdgroupFormData.objective_type" placeholder="请选择广告目标" style="width: 100%">
            <el-option label="应用推广" value="APP_PROMOTION" />
          </el-select>
        </el-form-item>

        <el-form-item label="预算模式" prop="budget_mode">
          <el-radio-group v-model="createAdgroupFormData.budget_mode">
            <el-radio label="BUDGET_MODE_DYNAMIC_DAILY_BUDGET">动态每日预算</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="预算金额" prop="budget">
          <el-input-number
              v-model="createAdgroupFormData.budget"
              :min="1"
              :step="1"
              placeholder="单位：元"
          />
          <span class="form-tip">¥{{ (createAdgroupFormData.budget || 0).toFixed(2) }}元/天</span>
        </el-form-item>

        <el-divider content-position="left">广告组基础配置</el-divider>

        <el-form-item label="推广类型" prop="promotion_type">
          <el-select v-model="createAdgroupFormData.promotion_type" placeholder="请选择" style="width: 100%">
            <el-option label="小游戏" value="MINI_GAME" />
          </el-select>
        </el-form-item>

        <el-form-item label="小游戏/小程序" prop="minis_id" v-if="createAdgroupFormData.promotion_type === 'MINI_GAME'">
          <el-select
              v-model="createAdgroupFormData.minis_id"
              placeholder="请选择小游戏/小程序"
              filterable
              style="width: 100%"
          >
            <el-option
                v-for="item in minisOptions"
                :key="item.minis_id"
                :label="item.minis_name"
                :value="item.minis_id"
            />
          </el-select>
          <el-button type="text" size="small" @click="loadMinisOptions" :loading="minisLoading">
            刷新列表
          </el-button>
        </el-form-item>

        <el-form-item label="优化目标" prop="optimization_goal">
          <el-select v-model="createAdgroupFormData.optimization_goal" placeholder="请选择" style="width: 100%">
            <el-option label="价值优化" value="VALUE" />
          </el-select>
        </el-form-item>

        <el-divider content-position="left">出价与预算</el-divider>

        <el-form-item label="出价策略" prop="bid_type">
          <el-radio-group v-model="createAdgroupFormData.bid_type">
            <el-radio label="BID_TYPE_NO_BID">最大转化（系统自动出价）</el-radio>
            <el-radio label="BID_TYPE_CUSTOM">成本控制（手动出价）</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
            v-if="createAdgroupFormData.bid_type === 'BID_TYPE_CUSTOM'"
            label="目标出价"
            prop="conversion_bid_price"
        >
          <el-input-number
              v-model="createAdgroupFormData.conversion_bid_price"
              :min="100"
              :step="50"
              placeholder="单位：分"
          />
          <span class="form-tip">分/转化，¥{{ ((createAdgroupFormData.conversion_bid_price || 0) / 100).toFixed(2) }}元</span>
        </el-form-item>

        <!-- 价值优化 (VBO) -->
        <template v-if="createAdgroupFormData.optimization_goal === 'VALUE'">
          <el-form-item label="深度出价策略" prop="deep_bid_type">
            <el-select v-model="createAdgroupFormData.deep_bid_type" placeholder="请选择" style="width: 200px">
              <el-option label="最小ROAS" value="VO_MIN_ROAS" />
              <el-option label="最高价值" value="VO_HIGHEST_VALUE" />
            </el-select>
          </el-form-item>

          <el-form-item v-if="createAdgroupFormData.deep_bid_type === 'VO_MIN_ROAS'" label="目标ROAS" prop="roas_bid">
            <el-input-number
                v-model="createAdgroupFormData.roas_bid"
                :min="0.01"
                :max="1000"
                :step="0.01"
                :precision="2"
                placeholder="0.01-1000"
            />
            <span class="form-tip">例如：1.5 表示期望 150% 的回报</span>
          </el-form-item>
        </template>

        <el-divider content-position="left">投放排期</el-divider>

        <el-form-item label="排期类型" prop="schedule_type">
          <el-radio-group v-model="createAdgroupFormData.schedule_type">
            <el-radio label="SCHEDULE_FROM_NOW">立即开始，长期投放</el-radio>
            <el-radio label="SCHEDULE_START_END">指定开始和结束时间</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="开始时间" prop="schedule_start_time">
          <el-date-picker
              v-model="createAdgroupFormData.schedule_start_time"
              type="datetime"
              placeholder="选择开始时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              :picker-options="startTimeOptions"
          />
        </el-form-item>

        <el-form-item
            v-if="createAdgroupFormData.schedule_type === 'SCHEDULE_START_END'"
            label="结束时间"
            prop="schedule_end_time"
        >
          <el-date-picker
              v-model="createAdgroupFormData.schedule_end_time"
              type="datetime"
              placeholder="选择结束时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              :picker-options="endTimeOptions"
          />
        </el-form-item>

        <el-divider content-position="left">投放位置</el-divider>

        <el-form-item label="投放策略" prop="placement_type">
          <el-radio-group v-model="createAdgroupFormData.placement_type">
<!--            <el-radio label="PLACEMENT_TYPE_AUTOMATIC">自动投放</el-radio>-->
            <el-radio label="PLACEMENT_TYPE_NORMAL">手动选择</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
            v-if="createAdgroupFormData.placement_type === 'PLACEMENT_TYPE_NORMAL'"
            label="选择位置"
            prop="placements"
        >
          <el-checkbox-group v-model="createAdgroupFormData.placements">
            <el-checkbox label="PLACEMENT_TIKTOK">TikTok</el-checkbox>
            <el-checkbox label="PLACEMENT_PANGLE">Pangle</el-checkbox>
            <el-checkbox label="PLACEMENT_GLOBAL_APP_BUNDLE">Global App Bundle</el-checkbox>
          </el-checkbox-group>
        </el-form-item>

        <!-- 定向设置 -->
        <el-divider content-position="left">定向设置</el-divider>

        <el-form-item label="地理位置">
          <el-select
              v-model="createAdgroupFormData.targeting_location_ids"
              multiple
              filterable
              collapse-tags
              placeholder="请选择地理位置"
              style="width: 100%"
          >
            <el-option
                v-for="item in locationOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="性别定向">
          <el-select
              v-model="createAdgroupFormData.targeting_gender"
              placeholder="请选择性别定向"
              style="width: 100%"
          >
            <el-option
                v-for="(label, value) in genderOptions"
                :key="value"
                :label="label"
                :value="value"
            />
          </el-select>
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="createAdgroupDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleBatchCreateAdgroups" :loading="createAdgroupLoading">
          创建
        </el-button>
      </span>
    </el-dialog>

    <!-- 提交结果弹窗 -->
    <el-dialog
        title="创建结果"
        :visible.sync="submitResultDialogVisible"
        width="700px"
        :close-on-click-modal="false"
    >
      <el-result
          :icon="submitResults.failList.length === 0 ? 'success' : (submitResults.successList.length > 0 ? 'warning' : 'error')"
          :title="submitResults.failList.length === 0 ? '创建成功' : (submitResults.successList.length > 0 ? '部分成功' : '创建失败')"
          :sub-title="`成功: ${submitResults.successList.length} 个, 失败: ${submitResults.failList.length} 个`"
      />

      <el-descriptions v-if="submitResults.batchId" :column="4" size="small" border class="submit-result-meta">
        <el-descriptions-item label="批次号">{{ submitResults.batchId }}</el-descriptions-item>
        <el-descriptions-item label="总数">{{ submitResults.total }}</el-descriptions-item>
        <el-descriptions-item label="并发">{{ submitResults.concurrency }}</el-descriptions-item>
        <el-descriptions-item label="耗时">{{ formatDuration(submitResults.durationMs) }}</el-descriptions-item>
      </el-descriptions>

      <el-tabs v-model="activeResultTab">
        <el-tab-pane :label="`成功 (${submitResults.successList.length})`" name="success">
          <el-table :data="submitResults.successList" max-height="300" size="small">
            <el-table-column prop="adName" label="广告名称" min-width="200"></el-table-column>
            <el-table-column prop="adId" label="广告ID" width="180"></el-table-column>
            <el-table-column prop="adgroupName" label="广告组" width="150"></el-table-column>
            <el-table-column prop="durationText" label="耗时" width="90"></el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane :label="`失败 (${submitResults.failList.length})`" name="fail">
          <el-table :data="submitResults.failList" max-height="300" size="small">
            <el-table-column prop="adIndex" label="序号" width="80"></el-table-column>
            <el-table-column prop="error" label="失败原因" min-width="250"></el-table-column>
            <el-table-column prop="durationText" label="耗时" width="90"></el-table-column>
            <el-table-column label="操作" width="100">
              <template slot-scope="{ row }">
                <el-button v-if="row.canRetry" type="text" @click="retryCreateAd(row)">重试</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>

      <span slot="footer">
        <el-button @click="submitResultDialogVisible = false">关闭</el-button>
        <el-button v-if="submitResults.batchId" icon="el-icon-document" @click="openAdCreateLogPage">
          查看日志
        </el-button>
        <el-button v-if="submitResults.failList.some(r => r.canRetry)" type="primary" @click="retryAllFailedAds">
          重试所有失败
        </el-button>
      </span>
    </el-dialog>

    <!-- 服务器素材查询抽屉 -->
    <el-drawer
        :visible.sync="serverMaterialsDrawerVisible"
        direction="rtl"
        size="80%"
        append-to-body
    >
      <div class="drawer-content">
        <video-record-query
            ref="videoRecordQuery"
            :base-url="baseUrl"
            :advertiser-id="selectedAdvertiserIds[0]"
            @videos-assigned="handleVideosAssigned"
        />
      </div>
    </el-drawer>

    <!-- 保存配置弹窗 -->
    <el-dialog
        title="保存配置"
        :visible.sync="saveConfigDialogVisible"
        width="500px"
        @close="handleSaveConfigDialogClose"
    >
      <el-form ref="saveConfigForm" :model="saveConfigFormData" :rules="saveConfigFormRules" label-width="100px">
        <el-form-item label="配置名称" prop="name">
          <el-input v-model="saveConfigFormData.name" placeholder="请输入配置名称，如：日常投放配置" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="配置说明" prop="description">
          <el-input v-model="saveConfigFormData.description" type="textarea" :rows="3" placeholder="可选：添加配置说明，方便后续识别" maxlength="200" show-word-limit />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="saveConfigDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveConfig" :loading="saveConfigLoading">保存</el-button>
      </span>
    </el-dialog>

    <!-- 加载配置弹窗 -->
    <el-dialog
        title="加载历史配置"
        :visible.sync="loadConfigDialogVisible"
        width="700px"
    >
      <el-empty v-if="savedConfigs.length === 0" description="暂无保存的配置">
        <el-button type="primary" size="small" @click="openSaveConfigDialog">立即保存配置</el-button>
      </el-empty>
      <div v-else>
        <el-table :data="savedConfigs" size="small" border stripe>
          <el-table-column prop="name" label="配置名称" min-width="150" show-overflow-tooltip />
          <el-table-column prop="description" label="说明" min-width="200" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <span v-if="row.description">{{ row.description }}</span>
              <el-tag v-else size="mini" type="info">无说明</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="保存时间" width="160" />
          <el-table-column label="操作" width="200" >
            <template slot-scope="{ row, $index }">
              <el-button type="text" size="small" icon="el-icon-check" @click="handleLoadConfig(row)">加载</el-button>
              <el-button type="text" size="small" icon="el-icon-document-copy" @click="handleDuplicateConfig(row)">复制</el-button>
              <el-button type="text" size="small" icon="el-icon-delete" style="color: #f56c6c" @click="handleDeleteConfig($index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer">
        <el-button @click="loadConfigDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 视频预览弹窗 -->
    <el-dialog
        title="视频预览"
        :visible.sync="videoPreviewDialogVisible"
        width="800px"
        append-to-body
    >
      <div v-if="currentPreviewRow" class="video-preview-dialog">
        <!-- 广告信息 -->
        <div class="preview-ad-info">
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="广告主">{{ currentPreviewRow.advertiserName }}</el-descriptions-item>
            <el-descriptions-item label="广告组">{{ currentPreviewRow.adgroupName }}</el-descriptions-item>
            <el-descriptions-item label="广告名称" :span="2">{{ currentPreviewRow.adName }}</el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 视频列表 -->
        <div class="preview-video-list">
          <div class="preview-title">分配的视频 ({{ currentPreviewRow.videoThumbnails.length }}个)</div>
          <div class="video-grid-preview">
            <div
                v-for="(videoUrl, idx) in currentPreviewRow.videoThumbnails"
                :key="idx"
                class="video-preview-item"
            >
              <div class="video-index-badge">#{{ idx + 1 }}</div>
              <video :src="videoUrl" controls preload="none" muted></video>
              <div class="video-filename">{{ getVideoFileNameByIndex(currentPreviewRow.videoStartIndex + idx) }}</div>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer">
        <el-button @click="videoPreviewDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { adApi, videoApi, ctaApi, imageApi } from '@/api/ad'
import { adgroupApi } from '@/api/adgroup'
import { advertiserApi } from '@/api/advertiserApi'
import { appApi as catalogApi } from '@/api/app'
import { campaignApi } from '@/api/campaign'
import VideoRecordQuery from '@/components/views/ads/VideoRecordQuery.vue'

export default {
  name: 'BatchAdCreator',
  components: {
    VideoRecordQuery
  },
  data() {
    return {
      // 基础配置
      baseUrl: 'https://tiktoktf.darknight.games/api/api',
      VIDEO_LIBRARY_STORAGE_KEY: 'global_video_library',
      CONFIG_STORAGE_KEY: 'batch_ad_creator_configs',
      BATCH_CREATE_CONCURRENCY: 4,
      
      // 标记是否已初始化
      hasInitialized: false,
      
      // 性能优化：跟踪hover状态
      hoveredVideoIndex: null,
      
      // 性能优化：防抖定时器
      saveTimer: null,

      // 广告主和广告组
      advertisers: [],
      selectedAdvertiserIds: [],
      advertiserConfigs: [],
      adgroupLoading: false,

      // 视频素材
      globalVideoLibrary: [],
      materialReviewLoading: false,
      deleteRejectedLoading: false,

      // 广告配置
      adConfig: {
        adNamePrefix: '',
        adTexts: [{ content: '' }],
        selectedCtas: [],
        landingPageUrl: '',
        operationStatus: 'ENABLE',
        videosPerAd: 1,
        selectedIdentity: ''
      },

      // 广告发布者列表
      identityList: [],
      identityLoading: false,

      // 推荐文案
      ctaRecommendList: [],
      ctaLoading: false,

      // 新建广告组
      createAdgroupDialogVisible: false,
      createAdgroupLoading: false,
      minisOptions: [],
      minisLoading: false,
      locationOptions: [
        { id: '6252001', name: '美国' },
        { id: '6089730', name: '印度尼西亚' },
        { id: '6089729', name: '日本' },
        { id: '6089731', name: '韩国' },
        { id: '6089732', name: '泰国' },
        { id: '6089733', name: '越南' },
        { id: '6089734', name: '菲律宾' },
        { id: '6089735', name: '马来西亚' },
        { id: '6089736', name: '新加坡' },
        { id: '6089737', name: '台湾' },
        { id: '6089738', name: '香港' }
      ],
      genderOptions: {
        'GENDER_UNLIMITED': '不限',
        'GENDER_MALE': '男性',
        'GENDER_FEMALE': '女性'
      },
      createAdgroupFormData: {
        targetAdvertisers: [],
        campaignCount: 1,
        adgroupCount: 1,
        objective_type: 'APP_PROMOTION',
        budget_mode: 'BUDGET_MODE_DYNAMIC_DAILY_BUDGET',
        budget: 50,
        promotion_type: 'MINI_GAME',
        minis_id: '',
        optimization_goal: 'VALUE',
        bid_type: 'BID_TYPE_NO_BID',
        conversion_bid_price: 100,
        bid_price: 100,
        deep_bid_type: '',
        roas_bid: null,
        schedule_type: 'SCHEDULE_FROM_NOW',
        schedule_start_time: '',
        schedule_end_time: '',
        placement_type: 'PLACEMENT_TYPE_NORMAL',
        placements: ['PLACEMENT_TIKTOK'],
        targeting_location_ids: ['6252001'],
        targeting_languages: ['en'],
        targeting_gender: 'GENDER_UNLIMITED'
      },
      createAdgroupFormRules: {
        targetAdvertisers: [{ required: true, message: '请选择目标广告主', trigger: 'change', type: 'array' }],
        campaignCount: [{ required: true, message: '请输入系列创建数量', trigger: 'blur' }],
        adgroupCount: [{ required: true, message: '请输入广告组创建数量', trigger: 'blur' }],
        objective_type: [{ required: true, message: '请选择广告目标', trigger: 'change' }],
        budget: [{ required: true, message: '请输入预算金额', trigger: 'blur' }],
        promotion_type: [{ required: true, message: '请选择推广类型', trigger: 'change' }],
        minis_id: [{ required: true, message: '请选择小游戏', trigger: 'change' }],
        optimization_goal: [{ required: true, message: '请选择优化目标', trigger: 'change' }],
        schedule_start_time: [{ required: true, message: '请选择开始时间', trigger: 'change' }]
      },
      startTimeOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 28.64e7
        }
      },
      endTimeOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7
        }
      },

      // 提交相关
      submitLoading: false,
      submitResultDialogVisible: false,
      activeResultTab: 'success',
      submitProgress: {
        batchId: '',
        total: 0,
        completed: 0,
        success: 0,
        fail: 0,
        concurrency: 4
      },
      submitResults: {
        successList: [],
        failList: [],
        batchId: '',
        total: 0,
        concurrency: 0,
        durationMs: 0
      },

      // 素材抽屉
      serverMaterialsDrawerVisible: false,

      // 视频预览弹窗
      videoPreviewDialogVisible: false,
      currentPreviewRow: null,

      // 配置管理
      saveConfigDialogVisible: false,
      saveConfigLoading: false,
      loadConfigDialogVisible: false,
      savedConfigs: [],
      saveConfigFormData: {
        name: '',
        description: ''
      },
      saveConfigFormRules: {
        name: [{ required: true, message: '请输入配置名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    selectedVideos() {
      return this.globalVideoLibrary.filter(v => v.selected)
    },

    isAllSelected() {
      if (this.globalVideoLibrary.length === 0) return false
      return this.globalVideoLibrary.every(v => v.selected)
    },

    materialReviewStats() {
      return this.globalVideoLibrary.reduce((stats, video) => {
        const summary = video.reviewSummary || {}
        stats.passed += Number(summary.passed || 0)
        stats.rejected += Number(summary.rejected || 0)
        stats.pending += Number(summary.pending || 0)
        stats.partial += Number(summary.partial || 0)
        stats.unknown += Number(summary.unknown || 0)
        stats.noAdMaterial += Number(summary.noAdMaterial || 0)
        return stats
      }, { passed: 0, rejected: 0, pending: 0, partial: 0, unknown: 0, noAdMaterial: 0 })
    },

    rejectedReviewedVideos() {
      return this.globalVideoLibrary.filter(video => Number(video.reviewSummary?.rejected || 0) > 0)
    },

    totalAdgroupsCount() {
      return this.advertiserConfigs.reduce((sum, config) => sum + (config.adgroupIds?.length || 0), 0)
    },

    totalAdsCount() {
      return this.advertiserConfigs.reduce((sum, config) => {
        return sum + (config.adgroupIds?.length || 0) * (config.adsPerAdgroup || 1)
      }, 0)
    },

    canAutoCalculate() {
      return this.selectedVideos.length > 0 && this.totalAdgroupsCount > 0
    },

    canSubmit() {
      return this.selectedVideos.length > 0 &&
          this.totalAdgroupsCount > 0 &&
          this.adConfig.videosPerAd > 0 &&
          this.totalAdsCount > 0 &&
          this.totalAdsCount * this.adConfig.videosPerAd <= this.selectedVideos.length &&
          this.adConfig.selectedIdentity // 必须选择广告发布者
    },

    submitProgressPercent() {
      if (!this.submitProgress.total) return 0
      return Math.min(100, Math.round((this.submitProgress.completed / this.submitProgress.total) * 100))
    },

    previewStatus() {
      if (this.selectedVideos.length === 0) {
        return { type: 'warning', text: '请选择视频素材' }
      }
      if (this.totalAdgroupsCount === 0) {
        return { type: 'warning', text: '请选择广告组' }
      }
      const neededVideos = this.totalAdsCount * this.adConfig.videosPerAd
      if (neededVideos > this.selectedVideos.length) {
        return { type: 'danger', text: `视频不足，需要${neededVideos}个，当前${this.selectedVideos.length}个` }
      }
      return { type: 'success', text: '配置就绪' }
    },

    adDistributionPreview() {
      const preview = []
      const videos = this.selectedVideos
      const videosPerAd = this.adConfig.videosPerAd
      let videoIndex = 0

      for (const config of this.advertiserConfigs) {
        const advertiserName = this.getAdvertiserName(config.advertiserId)
        const adgroups = config.availableAdgroups.filter(ag => config.adgroupIds.includes(ag.adgroup_id))

        for (const adgroup of adgroups) {
          const ads = []
          for (let i = 0; i < (config.adsPerAdgroup || 1); i++) {
            if (videoIndex + videosPerAd > videos.length) break

            const adVideos = videos.slice(videoIndex, videoIndex + videosPerAd)
            const now = new Date()
            const dateTimeStr = this.formatDateTime(now)
            const username = this.$store.state.username || 'unknown'
            const baseName = this.adConfig.adNamePrefix || '广告'
            const adName = `${baseName}_${advertiserName}_${adgroup.adgroup_name}_${i + 1}`

            // 性能优化：只生成缩略图URL，不预加载
            const videoThumbnails = adVideos.map(v => this.getVideoThumbnail(v)).filter(url => url)

            ads.push({
              adName,
              videoStartIndex: videoIndex,
              videoEndIndex: videoIndex + videosPerAd - 1,
              videoNames: adVideos.map(v => this.getVideoFileName(v)).join(', '),
              videoThumbnails: videoThumbnails
            })

            videoIndex += videosPerAd
          }

          if (ads.length > 0) {
            preview.push({
              advertiserName,
              adgroupName: adgroup.adgroup_name,
              ads
            })
          }
        }
      }

      return preview
    },

    flattenedPreviewData() {
      const flattened = []
      for (const group of this.adDistributionPreview) {
        for (const ad of group.ads) {
          flattened.push({
            advertiserName: group.advertiserName,
            adgroupName: group.adgroupName,
            adName: ad.adName,
            videoStartIndex: ad.videoStartIndex,
            videoEndIndex: ad.videoEndIndex,
            videoNames: ad.videoNames,
            videoThumbnails: ad.videoThumbnails || []
          })
        }
      }
      return flattened
    }
  },
  mounted() {
    // 首次加载时初始化
    if (!this.hasInitialized) {
      this.initData()
      this.fetchCtaRecommendations()
      this.loadVideoLibraryFromCache() // 只在首次加载时恢复缓存
      this.hasInitialized = true
    }
    
    // 调试：检查视频数据结构
    this.$nextTick(() => {
      if (this.globalVideoLibrary.length > 0) {
        console.log('=== 视频库数据结构调试 ===')
        console.log('视频总数:', this.globalVideoLibrary.length)
        console.log('第一个视频:', this.globalVideoLibrary[0])
        if (this.globalVideoLibrary[0].creative_info) {
          console.log('creative_info:', this.globalVideoLibrary[0].creative_info)
          console.log('video_info:', this.globalVideoLibrary[0].creative_info.video_info)
        }
      } else {
        console.warn('视频库为空，请先添加视频素材')
      }
      
      // 打印内存使用情况
      this.logMemoryUsage()
    })
  },
  activated() {
    // 组件被激活时,不需要做任何事,keep-alive会自动保持状态
    console.log('BatchAdCreator activated - 状态已自动保持')
  },
  beforeDestroy() {
    // 清理定时器
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
      this.saveTimer = null
    }
  },
  methods: {
    async initData() {
      await this.fetchAdvertisers()
    },

    loadVideoLibraryFromCache() {
      try {
        const cached = localStorage.getItem(this.VIDEO_LIBRARY_STORAGE_KEY)
        if (cached) {
          const parsed = JSON.parse(cached)
          if (Array.isArray(parsed)) {
            this.globalVideoLibrary = parsed
          }
        }
      } catch (error) {
        console.error('加载视频素材库缓存失败', error)
      }
    },

    saveVideoLibraryToCache() {
      try {
        localStorage.setItem(this.VIDEO_LIBRARY_STORAGE_KEY, JSON.stringify(this.globalVideoLibrary))
      } catch (error) {
        console.error('保存视频素材库缓存失败', error)
      }
    },

    toggleVideoSelection(index) {
      // 使用Vue.set确保响应式更新更高效
      this.$set(this.globalVideoLibrary[index], 'selected', !this.globalVideoLibrary[index].selected)
      // 延迟保存，避免频繁写入localStorage
      if (!this.saveTimer) {
        this.saveTimer = setTimeout(() => {
          this.saveVideoLibraryToCache()
          this.saveTimer = null
        }, 500)
      }
    },

    selectAllVideos() {
      const shouldSelect = !this.isAllSelected
      // 批量更新，减少响应式触发次数
      this.globalVideoLibrary.forEach(v => {
        v.selected = shouldSelect
      })
      // 强制更新视图
      this.$forceUpdate()
      this.saveVideoLibraryToCache()
    },

    shuffleGlobalVideoLibrary() {
      if (this.globalVideoLibrary.length < 2) {
        return
      }

      const shuffledVideos = [...this.globalVideoLibrary]
      for (let i = shuffledVideos.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[shuffledVideos[i], shuffledVideos[j]] = [shuffledVideos[j], shuffledVideos[i]]
      }

      this.globalVideoLibrary = shuffledVideos
      this.hoveredVideoIndex = null
      this.saveVideoLibraryToCache()
      this.$message.success('素材顺序已打乱')
    },

    removeSelectedVideos() {
      this.$confirm(`确定要删除选中的 ${this.selectedVideos.length} 个视频吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.globalVideoLibrary = this.globalVideoLibrary.filter(v => !v.selected)
        this.saveVideoLibraryToCache()
        this.$message.success('已删除选中的视频')
      })
    },

    clearGlobalLibrary() {
      this.$confirm('确定要清空所有视频素材吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.globalVideoLibrary = []
        localStorage.removeItem(this.VIDEO_LIBRARY_STORAGE_KEY)
        this.$message.success('素材库已清空')
      })
    },

    getVideoUrl(video) {
      if (!video || !video.creative_info) return ''
      const videoInfo = video.creative_info.video_info || {}
      return videoInfo.preview_url || videoInfo.video_url || ''
    },

    // 获取视频缩略图(第一帧)
    getVideoThumbnail(video) {
      if (!video || !video.creative_info) {
        // console.warn('视频或creative_info为空:', video)
        return ''
      }
      const videoInfo = video.creative_info.video_info || {}
          
      // 优先使用封面图
      if (videoInfo.video_cover_url) {
        // console.log('使用封面图:', videoInfo.video_cover_url)
        return videoInfo.video_cover_url
      }
          
      // 其次使用preview_url
      const fallbackUrl = videoInfo.preview_url || videoInfo.video_url || ''
      if (fallbackUrl) {
        console.log('使用预览URL:', fallbackUrl)
      } else {
        console.warn('没有找到任何可用的URL:', videoInfo)
      }
      return fallbackUrl
    },

    isVideoHovered(index) {
      return this.hoveredVideoIndex === index
    },

    handleVideoMouseEnter(index) {
      this.hoveredVideoIndex = index
    },

    handleVideoMouseLeave() {
      this.hoveredVideoIndex = null
    },

    // 处理图片加载失败
    handleImageError(event, video) {
      console.error('图片加载失败:', event.target.src, video)
      // 设置一个默认的占位图
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmN2ZhIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzkwOTM5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuWbvuS/oeaBrzwvdGV4dD48L3N2Zz4='
    },

    getVideoFileName(video) {
      return video.file_name || '未命名视频'
    },

    getVideoFileNameByIndex(index) {
      const video = this.selectedVideos[index]
      return video ? this.getVideoFileName(video) : '未知视频'
    },

    showVideoPreview(row) {
      this.currentPreviewRow = row
      this.videoPreviewDialogVisible = true
    },

    showServerMaterials() {
      this.serverMaterialsDrawerVisible = true
    },

    handleVideosAssigned(assignmentData) {
      const { videos } = assignmentData
      if (!videos || videos.length === 0) {
        this.$message.warning('没有可分配的视频')
        return
      }

      this.serverMaterialsDrawerVisible = false

      let addedCount = 0
      let skippedCount = 0

      videos.forEach((video, index) => {
        const videoId = video.creative_info.video_info.video_id
        const exists = this.globalVideoLibrary.some(v => {
          const existingVideoId = v.creative_info?.video_info?.video_id
          return existingVideoId === videoId
        })

        if (exists) {
          skippedCount++
          return
        }

        this.globalVideoLibrary.push(video)
        addedCount++
      })

      this.saveVideoLibraryToCache()

      let message = `成功添加 ${addedCount} 个视频`
      if (skippedCount > 0) message += `，跳过 ${skippedCount} 个重复视频`
      this.$message.success(message)
    },

    async refreshGlobalMaterialReview() {
      const payload = this.buildMaterialReviewPayload(this.globalVideoLibrary)
      if (payload.videoRecordIds.length === 0 && payload.materialIds.length === 0 && payload.videoIds.length === 0) {
        this.$message.warning('当前素材缺少数据库记录ID、素材ID或视频ID，无法查询审核统计')
        return
      }

      this.materialReviewLoading = true
      try {
        const res = await adApi.getMaterialReviewInfo(payload)
        const data = res.data || {}
        this.applyMaterialReviewSummaries(this.globalVideoLibrary, data.sourceSummaries || [])
        this.saveVideoLibraryToCache()
        this.$message.success(`审核统计已刷新：通过 ${data.passed || 0}，失败 ${data.rejected || 0}`)
      } catch (error) {
        console.error('刷新素材审核统计失败', error)
        this.$message.error('刷新素材审核统计失败: ' + error.message)
      } finally {
        this.materialReviewLoading = false
      }
    },

    async deleteRejectedReviewedVideos() {
      const rejectedVideos = this.rejectedReviewedVideos
      if (rejectedVideos.length === 0) {
        this.$message.warning('当前没有已统计为不过审的素材')
        return
      }

      try {
        await this.$confirm(`确定要删除 ${rejectedVideos.length} 个存在审核失败记录的素材吗？`, '删除不过审素材', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const payload = this.buildDeleteByMaterialPayload(rejectedVideos)
        this.deleteRejectedLoading = true
        await videoApi.deleteByMaterials({
          ...payload,
          deletedBy: this.$store.state.username || this.$store.state.user?.username || '',
          reason: '审核失败'
        })

        const rejectedKeys = new Set(rejectedVideos.map(video => this.getVideoUniqueKey(video)))
        this.globalVideoLibrary = this.globalVideoLibrary.filter(video => !rejectedKeys.has(this.getVideoUniqueKey(video)))
        this.saveVideoLibraryToCache()
        this.$message.success('不过审素材已删除')
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('删除不过审素材失败: ' + error.message)
        }
      } finally {
        this.deleteRejectedLoading = false
      }
    },

    buildMaterialReviewPayload(videos) {
      return {
        videoRecordIds: [...new Set((videos || []).map(video => video.dbRecordId || video.id).filter(Boolean))],
        materialIds: [...new Set((videos || []).map(video => this.getUploadMaterialId(video)).filter(Boolean))],
        videoIds: [...new Set((videos || []).map(video => this.getVideoId(video)).filter(Boolean))],
        includeRejectInfo: true
      }
    },

    buildDeleteByMaterialPayload(videos) {
      return {
        materialIds: [...new Set((videos || []).map(video => this.getUploadMaterialId(video)).filter(Boolean))],
        videoIds: [...new Set((videos || []).map(video => this.getVideoId(video)).filter(Boolean))]
      }
    },

    applyMaterialReviewSummaries(videos, summaries) {
      const summaryMap = new Map()
      ;(summaries || []).forEach(summary => {
        if (summary.sourceRecordId) summaryMap.set(`record:${summary.sourceRecordId}`, summary)
        if (summary.sourceUploadMaterialId) summaryMap.set(`material:${summary.sourceUploadMaterialId}`, summary)
        if (summary.sourceVideoId) summaryMap.set(`video:${summary.sourceVideoId}`, summary)
      })

      videos.forEach(video => {
        const summary = summaryMap.get(`record:${video.dbRecordId || video.id}`) ||
            summaryMap.get(`material:${this.getUploadMaterialId(video)}`) ||
            summaryMap.get(`video:${this.getVideoId(video)}`) ||
            { total: 0, passed: 0, rejected: 0, pending: 0, partial: 0, unknown: 0, noAdMaterial: 1 }
        this.$set(video, 'reviewSummary', summary)
      })
    },

    getUploadMaterialId(video) {
      return video?.materialId || video?.uploadMaterialId || video?.creative_info?.video_info?.material_id || ''
    },

    getVideoId(video) {
      return video?.videoId || video?.creative_info?.video_info?.video_id || ''
    },

    getVideoUniqueKey(video) {
      return String(video?.dbRecordId || video?.id || this.getUploadMaterialId(video) || this.getVideoId(video) || video?.file_name || '')
    },

    getVideoReviewTagType(video) {
      const summary = video.reviewSummary
      if (!summary) return 'info'
      if (Number(summary.rejected || 0) > 0) return 'danger'
      if (Number(summary.pending || 0) > 0) return 'warning'
      if (Number(summary.passed || 0) > 0) return 'success'
      return 'info'
    },

    getVideoReviewText(video) {
      const summary = video.reviewSummary
      if (!summary) return '未刷新'
      return `过${summary.passed || 0}/败${summary.rejected || 0}/审${summary.pending || 0}`
    },

    async fetchAdvertisers() {
      try {
        const res = await advertiserApi.getList({ page: 1, pageSize: 100 })
        if (res.code === 0) {
          this.advertisers = res.data.list || []
        }
      } catch (error) {
        console.error('获取广告主列表失败', error)
      }
    },

    getAdvertiserName(advertiserId) {
      const advertiser = this.advertisers.find(item => item.advertiser_id === advertiserId)
      return advertiser ? advertiser.advertiser_name : ''
    },

    async handleAdvertisersChange(selectedIds) {
      this.advertiserConfigs = this.advertiserConfigs.filter(config =>
          selectedIds.includes(config.advertiserId)
      )

      for (const id of selectedIds) {
        const exists = this.advertiserConfigs.some(config => config.advertiserId === id)
        if (!exists) {
          const newConfig = {
            advertiserId: id,
            adgroupIds: [],
            availableAdgroups: [],
            adsPerAdgroup: 1
          }
          this.advertiserConfigs.push(newConfig)
          await this.fetchAdgroupsForAdvertiser(newConfig)
        }
      }

      // 当广告主变化时，获取第一个广告主的发布者列表
      if (selectedIds.length > 0) {
        await this.fetchIdentityList(selectedIds[0])
      } else {
        this.identityList = []
        this.adConfig.selectedIdentity = ''
      }
    },

    async fetchAdgroupsForAdvertiser(config) {
      this.adgroupLoading = true
      try {
        const res = await adgroupApi.getList({
          advertiserId: config.advertiserId,
          page: 1,
          pageSize: 100
        })
        if (res.code === 0) {
          config.availableAdgroups = res.data.list || []
        }
      } catch (error) {
        console.error('获取广告组列表失败', error)
      } finally {
        this.adgroupLoading = false
      }
    },

    removeAdvertiser(index) {
      const config = this.advertiserConfigs[index]
      const idIndex = this.selectedAdvertiserIds.indexOf(config.advertiserId)
      if (idIndex > -1) {
        this.selectedAdvertiserIds.splice(idIndex, 1)
      }
      this.advertiserConfigs.splice(index, 1)
    },

    addAdText() {
      this.adConfig.adTexts.push({ content: '' })
    },

    removeAdText(index) {
      this.adConfig.adTexts.splice(index, 1)
    },

    autoCalculateVideosPerAd() {
      const totalVideos = this.selectedVideos.length
      const totalAds = this.totalAdsCount
      if (totalVideos === 0 || totalAds === 0) return

      const recommended = Math.floor(totalVideos / totalAds)
      this.adConfig.videosPerAd = Math.max(1, Math.min(recommended, 10))
      this.$message.success(`已自动设置为每广告 ${this.adConfig.videosPerAd} 个视频`)
    },

    async fetchCtaRecommendations() {
      try {
        this.ctaLoading = true
        const res = await ctaApi.getRecommendCta({
          advertiserId: '7598883753192357896',
          objectiveType: 'APP_PROMOTION',
          promotionType: 'MINI_GAME',
          language: 'en'
        })
        if (res.code === 0 && res.data?.recommend_assets) {
          this.ctaRecommendList = res.data.recommend_assets
        }
      } catch (error) {
        console.error('获取推荐文案失败', error)
      } finally {
        this.ctaLoading = false
      }
    },

    // 获取广告发布者列表
    async fetchIdentityList(advertiserId) {
      if (!advertiserId) return
      
      try {
        this.identityLoading = true
        const res = await this.$axios.get('https://tiktoktf.darknight.games/api/app/identity', {
          withCredentials:false,
          params: {
            advertiserId,
            identityType: 'BC_AUTH_TT'
          }
        })
        
        if (res.data.code === 0 && res.data.data?.identity_list) {
          this.identityList = res.data.data.identity_list.filter(item => item.available_status === 'AVAILABLE')
          
          // 如果有发布者且当前未选择，自动选择第一个
          if (this.identityList.length > 0 && !this.adConfig.selectedIdentity) {
            this.adConfig.selectedIdentity = this.identityList[0].identity_id
          }
        }
      } catch (error) {
        console.error('获取广告发布者列表失败', error)
        this.$message.error('获取广告发布者列表失败')
      } finally {
        this.identityLoading = false
      }
    },

    handleIdentityChange(identityId) {
      // 当选择变化时，可以在这里添加额外的处理逻辑
      console.log('选择的广告发布者:', identityId)
    },

    openCreateAdgroupDialog() {
      this.createAdgroupFormData.targetAdvertisers = [...this.selectedAdvertiserIds]
      this.createAdgroupFormData.schedule_start_time = this.formatDateTime(new Date())
      this.createAdgroupDialogVisible = true
      this.loadMinisOptions()
    },

    handleTargetAdvertisersChange() {
      // 当目标广告主变化时，重新加载小游戏列表
      this.loadMinisOptions()
    },

    handleCreateAdgroupDialogClose() {
      this.$refs.createAdgroupForm && this.$refs.createAdgroupForm.resetFields()
    },

    async loadMinisOptions() {
      try {
        this.minisLoading = true
        // 优先使用弹窗中选择的目标广告主，如果没有则不查询
        const advertiserId = this.createAdgroupFormData.targetAdvertisers?.[0]
        if (!advertiserId) {
          this.minisOptions = []
          return
        }

        const res = await catalogApi.getMinisList({
          advertiserId,
          page: 1,
          pageSize: 50
        })
        if (res.code === 0) {
          this.minisOptions = res.data.list || []
        }
      } catch (error) {
        console.error('获取小游戏列表失败', error)
      } finally {
        this.minisLoading = false
      }
    },

    async handleBatchCreateAdgroups() {
      try {
        await this.$refs.createAdgroupForm.validate()
        this.createAdgroupLoading = true

        const { targetAdvertisers, campaignCount, adgroupCount } = this.createAdgroupFormData
        const totalAdgroupsPerAdvertiser = campaignCount * adgroupCount
        this.$message.info(`正在为 ${targetAdvertisers.length} 个广告主创建，每个广告主将创建 ${campaignCount} 个系列，每个系列下 ${adgroupCount} 个广告组（共计 ${totalAdgroupsPerAdvertiser} 个广告组）...`)

        let totalSuccess = 0
        let totalFail = 0

        for (const advertiserId of targetAdvertisers) {
          let successCount = 0
          let failCount = 0
          const createdAdgroups = []  // 记录创建的广告组信息

          // 外层循环：创建广告系列
          for (let campaignIndex = 0; campaignIndex < campaignCount; campaignIndex++) {
            try {
              const campaignId = await this.createCampaign(advertiserId, campaignIndex)
              if (!campaignId) {
                failCount += adgroupCount
                continue
              }

              // 内层循环：为该系列创建N个广告组
              for (let adgroupIndex = 0; adgroupIndex < adgroupCount; adgroupIndex++) {
                try {
                  const adgroupId = await this.createAdgroup(advertiserId, campaignId, campaignIndex, adgroupIndex)
                  if (adgroupId) {
                    const dateTimeStr = this.formatDateTime(new Date())
                    const username = this.$store.state.username || 'unknown'
                    const baseName = this.adConfig.adNamePrefix || '广告'
                    const adgroupName = `${baseName}_${username}_${dateTimeStr}_系列${campaignIndex + 1}_组${adgroupIndex + 1}`

                    createdAdgroups.push({
                      adgroup_id: adgroupId,
                      adgroup_name: adgroupName
                    })
                    successCount++
                  } else {
                    failCount++
                  }
                } catch (error) {
                  console.error(`为广告主 ${advertiserId} 系列${campaignIndex + 1} 创建第 ${adgroupIndex + 1} 个广告组失败:`, error)
                  failCount++
                }
              }
            } catch (error) {
              console.error(`为广告主 ${advertiserId} 创建第 ${campaignIndex + 1} 个广告系列失败:`, error)
              failCount += adgroupCount
            }
          }

          totalSuccess += successCount
          totalFail += failCount

          const config = this.advertiserConfigs.find(c => c.advertiserId === advertiserId)
          if (config) {
            // 将新建的广告组添加到 availableAdgroups
            createdAdgroups.forEach(newAdgroup => {
              const exists = config.availableAdgroups.some(ag => ag.adgroup_id === newAdgroup.adgroup_id)
              if (!exists) {
                config.availableAdgroups.push(newAdgroup)
              }
              // 自动选中新建的广告组
              if (!config.adgroupIds.includes(newAdgroup.adgroup_id)) {
                config.adgroupIds.push(newAdgroup.adgroup_id)
              }
            })
          }
        }

        if (totalSuccess > 0) {
          this.$message.success(`成功创建 ${totalSuccess} 个广告组${totalFail > 0 ? `，${totalFail} 个失败` : ''}`)
          this.createAdgroupDialogVisible = false
        } else {
          this.$message.error('创建全部失败')
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.createAdgroupLoading = false
      }
    },

    async createCampaign(advertiserId, campaignIndex) {
      const timestamp = Date.now()
      const dateTimeStr = this.formatDateTime(new Date())
      const username = this.$store.state.username || 'unknown'
      const baseName = this.adConfig.adNamePrefix || '广告'
      const campaignName = `${baseName}_${username}_${dateTimeStr}_系列_${campaignIndex + 1}`

      const res = await campaignApi.create({
        advertiser_id: advertiserId,
        campaign_name: campaignName,
        objective_type: this.createAdgroupFormData.objective_type,
        campaign_type: 'REGULAR_CAMPAIGN',
        budget_optimize_on: true,
        budget_mode: this.createAdgroupFormData.budget_mode,
        budget: this.createAdgroupFormData.budget,
        request_id: `${timestamp}${campaignIndex}`,
        app_promotion_type: 'MINIS'
      })

      if (res.code === 0 && res.data?.campaign_id) {
        return res.data.campaign_id
      }
      throw new Error(res.message || '创建广告系列失败')
    },

    async createAdgroup(advertiserId, campaignId, campaignIndex, adgroupIndex) {
      const timestamp = Date.now()
      const dateTimeStr = this.formatDateTime(new Date())
      const username = this.$store.state.username || 'unknown'
      const baseName = this.adConfig.adNamePrefix || '广告'
      const adgroupName = `${baseName}_${username}_${dateTimeStr}_系列${campaignIndex + 1}_组${adgroupIndex + 1}`

      const adjustedStartTime = this.addHoursToDate(this.createAdgroupFormData.schedule_start_time, 5)

      const requestData = {
        advertiser_id: advertiserId,
        adgroup_name: adgroupName,
        campaign_id: campaignId,
        promotion_type: this.createAdgroupFormData.promotion_type,
        minis_id: this.createAdgroupFormData.minis_id,
        optimization_goal: this.createAdgroupFormData.optimization_goal,
        bid_type: this.createAdgroupFormData.bid_type,
        schedule_type: this.createAdgroupFormData.schedule_type,
        schedule_start_time: adjustedStartTime,
        placement_type: this.createAdgroupFormData.placement_type,
        optimization_event: 'AD_REVENUE_VALUE',
        billing_event: 'OCPM',
        vbo_window: 'ZERO_DAY',
        request_id: `${timestamp}${campaignIndex}${adgroupIndex}`
      }

      if (this.createAdgroupFormData.bid_type === 'BID_TYPE_CUSTOM') {
        requestData.conversion_bid_price = this.createAdgroupFormData.conversion_bid_price
        requestData.bid_price = this.createAdgroupFormData.bid_price
      }

      if (this.createAdgroupFormData.optimization_goal === 'VALUE') {
        if (this.createAdgroupFormData.deep_bid_type) {
          requestData.deep_bid_type = this.createAdgroupFormData.deep_bid_type
        }
        if (this.createAdgroupFormData.roas_bid) {
          requestData.roas_bid = this.createAdgroupFormData.roas_bid
        }
      }

      if (this.createAdgroupFormData.schedule_type === 'SCHEDULE_START_END') {
        requestData.schedule_end_time = this.createAdgroupFormData.schedule_end_time
      }

      if (this.createAdgroupFormData.placement_type === 'PLACEMENT_TYPE_NORMAL') {
        requestData.placements = this.createAdgroupFormData.placements
      }

      if (this.createAdgroupFormData.targeting_location_ids?.length > 0 || this.createAdgroupFormData.targeting_gender) {
        requestData.targeting_spec = {
          location_ids: this.createAdgroupFormData.targeting_location_ids || [],
          languages: ['en']
        }
        // 添加性别定向
        if (this.createAdgroupFormData.targeting_gender && this.createAdgroupFormData.targeting_gender !== 'GENDER_UNLIMITED') {
          requestData.targeting_spec.gender = this.createAdgroupFormData.targeting_gender
        }
      }

      const res = await adgroupApi.create(requestData)
      if (res.code === 0 && res.data?.adgroup_id) {
        return res.data.adgroup_id
      }
      throw new Error(res.message || '创建广告组失败')
    },

    async handleBatchSubmit() {
      if (!this.canSubmit) {
        this.$message.warning('请完善配置信息')
        return
      }

      this.submitLoading = true
      const batchId = this.createBatchTraceId()
      const batchStartTime = Date.now()
      const results = {
        successList: [],
        failList: [],
        batchId,
        total: 0,
        concurrency: this.BATCH_CREATE_CONCURRENCY,
        durationMs: 0
      }

      try {
        let callToActionId = null
        if (this.adConfig.selectedCtas.length > 0) {
          try {
            callToActionId = await this.createPortfolioFromSelectedCta()
          } catch (error) {
            this.$message.error('创建素材包失败: ' + error.message)
            this.submitLoading = false
            return
          }
        }

        const adConfigs = this.generateAdConfigs(callToActionId).map((config, index, list) => ({
          ...config,
          client_batch_id: batchId,
          client_batch_index: index + 1,
          client_batch_total: list.length,
          client_batch_concurrency: this.BATCH_CREATE_CONCURRENCY
        }))

        results.total = adConfigs.length
        this.resetSubmitProgress(batchId, adConfigs.length)
        console.info('[BatchAdCreate] start', {
          batchId,
          total: adConfigs.length,
          concurrency: this.BATCH_CREATE_CONCURRENCY
        })

        await this.runWithConcurrency(adConfigs, this.BATCH_CREATE_CONCURRENCY, async config => {
          const itemStartTime = Date.now()
          console.info('[BatchAdCreate] item start', {
            batchId,
            index: config.client_batch_index,
            total: config.client_batch_total,
            advertiserId: config.advertiserId,
            adgroupId: config.adgroup_id,
            adName: config.ad_name
          })

          try {
            const res = await adApi.create(config)
            const durationMs = Date.now() - itemStartTime
            if (res.code === 0) {
              results.successList.push({
                adName: config.ad_name,
                adId: res.data?.smart_plus_ad_id || '未知',
                adgroupName: config.adgroup_name,
                durationMs,
                durationText: this.formatDuration(durationMs)
              })
              this.updateSubmitProgress(true)
              console.info('[BatchAdCreate] item success', {
                batchId,
                index: config.client_batch_index,
                adId: res.data?.smart_plus_ad_id,
                durationMs
              })
            } else {
              results.failList.push({
                adIndex: config.ad_index,
                error: res.message || '创建失败',
                requestData: config,
                canRetry: true,
                durationMs,
                durationText: this.formatDuration(durationMs)
              })
              this.updateSubmitProgress(false)
              console.warn('[BatchAdCreate] item fail', {
                batchId,
                index: config.client_batch_index,
                message: res.message,
                durationMs
              })
            }
          } catch (error) {
            const durationMs = Date.now() - itemStartTime
            results.failList.push({
              adIndex: config.ad_index,
              error: error.message || '创建失败',
              requestData: config,
              canRetry: true,
              durationMs,
              durationText: this.formatDuration(durationMs)
            })
            this.updateSubmitProgress(false)
            console.error('[BatchAdCreate] item error', {
              batchId,
              index: config.client_batch_index,
              message: error.message,
              durationMs
            }, error)
          }
        })

        results.durationMs = Date.now() - batchStartTime
        console.info('[BatchAdCreate] finish', {
          batchId,
          total: results.total,
          success: results.successList.length,
          fail: results.failList.length,
          durationMs: results.durationMs
        })

        this.submitResults = results
        this.submitResultDialogVisible = true

        if (results.failList.length === 0) {
          this.$message.success(`成功创建 ${results.successList.length} 个广告`)
          this.globalVideoLibrary = []
          localStorage.removeItem(this.VIDEO_LIBRARY_STORAGE_KEY)
        }
      } catch (error) {
        this.$message.error('提交失败: ' + error.message)
      } finally {
        this.submitLoading = false
      }
    },

    createBatchTraceId() {
      const timestamp = this.formatDateTime(new Date()).replace(/[-:\s]/g, '')
      const random = Math.random().toString(36).slice(2, 8)
      return `ad-batch-${timestamp}-${random}`
    },

    resetSubmitProgress(batchId, total) {
      this.submitProgress = {
        batchId,
        total,
        completed: 0,
        success: 0,
        fail: 0,
        concurrency: Math.min(this.BATCH_CREATE_CONCURRENCY, total || this.BATCH_CREATE_CONCURRENCY)
      }
    },

    updateSubmitProgress(success) {
      this.submitProgress.completed += 1
      if (success) {
        this.submitProgress.success += 1
      } else {
        this.submitProgress.fail += 1
      }
    },

    async runWithConcurrency(items, limit, worker) {
      const workerCount = Math.min(Math.max(Number(limit) || 1, 1), items.length)
      let nextIndex = 0
      const runners = Array.from({ length: workerCount }, async () => {
        while (nextIndex < items.length) {
          const currentIndex = nextIndex
          nextIndex += 1
          await worker(items[currentIndex], currentIndex)
        }
      })
      await Promise.all(runners)
    },

    formatDuration(durationMs) {
      const ms = Number(durationMs) || 0
      if (ms < 1000) return `${ms}ms`
      return `${(ms / 1000).toFixed(1)}s`
    },

    generateAdConfigs(callToActionId) {
      const configs = []
      const videos = this.selectedVideos
      const videosPerAd = this.adConfig.videosPerAd
      let videoIndex = 0
      let adIndex = 1

      // 获取选中的发布者信息
      const selectedIdentityItem = this.identityList.find(item => item.identity_id === this.adConfig.selectedIdentity)

      for (const config of this.advertiserConfigs) {
        const adgroups = config.availableAdgroups.filter(ag => config.adgroupIds.includes(ag.adgroup_id))

        for (const adgroup of adgroups) {
          for (let i = 0; i < (config.adsPerAdgroup || 1); i++) {
            if (videoIndex + videosPerAd > videos.length) break

            const adVideos = videos.slice(videoIndex, videoIndex + videosPerAd)
            const advertiserName = this.getAdvertiserName(config.advertiserId)
            const now = new Date()
            const dateTimeStr = this.formatDateTime(now)
            const username = this.$store.state.username || 'unknown'
            const baseName = this.adConfig.adNamePrefix || '广告'
            const adName = `${baseName}_${advertiserName}_${adgroup.adgroup_name}_${i + 1}`

            // 处理视频素材，添加发布者信息
            const processedVideos = adVideos.map(video => {
              const processedVideo = JSON.parse(JSON.stringify(video))
              
              // 如果选中了发布者，更新 creative_info 中的身份信息
              if (selectedIdentityItem && processedVideo.creative_info) {
                processedVideo.creative_info.identity_type = selectedIdentityItem.identity_type
                processedVideo.creative_info.identity_id = selectedIdentityItem.identity_id
                processedVideo.creative_info.identity_authorized_bc_id = selectedIdentityItem.identity_authorized_bc_id
              }
              
              return processedVideo
            })

            const adConfig = {
              advertiserId: config.advertiserId,
              adgroup_id: adgroup.adgroup_id,
              ad_name: adName,
              creative_list: processedVideos,
              ad_text_list: this.adConfig.adTexts.filter(t => t.content).map(t => ({ ad_text: t.content })),
              landing_page_url_list: this.adConfig.landingPageUrl ? [{ landing_page_url: this.adConfig.landingPageUrl }] : [],
              operation_status: this.adConfig.operationStatus,
              ad_configuration: { dark_post_status: 'ON' },
              ad_index: adIndex++,
              adgroup_name: adgroup.adgroup_name
            }

            if (callToActionId) {
              adConfig.ad_configuration.call_to_action_id = callToActionId
            }

            configs.push(adConfig)
            videoIndex += videosPerAd
          }
        }
      }

      return configs
    },

    async createPortfolioFromSelectedCta() {
      const portfolioContent = this.adConfig.selectedCtas.map(content => {
        const recommendItem = this.ctaRecommendList.find(item => item.asset_content === content)
        return {
          asset_content: content,
          asset_ids: recommendItem ? recommendItem.asset_ids : []
        }
      })

      const res = await ctaApi.createPortfolio({
        advertiserId: this.selectedAdvertiserIds[0],
        portfolioContent
      })

      if (res.code === 0 && res.data?.creative_portfolio_id) {
        return res.data.creative_portfolio_id
      }
      throw new Error(res.message || '创建素材包失败')
    },

    async retryCreateAd(failItem) {
      if (!failItem.canRetry) return

      const retryStartTime = Date.now()
      const requestData = {
        ...failItem.requestData,
        client_batch_id: failItem.requestData.client_batch_id || this.submitResults.batchId || this.createBatchTraceId(),
        client_batch_index: failItem.requestData.client_batch_index || failItem.adIndex,
        client_batch_total: failItem.requestData.client_batch_total || this.submitResults.total || 1,
        client_batch_concurrency: failItem.requestData.client_batch_concurrency || this.submitResults.concurrency || this.BATCH_CREATE_CONCURRENCY
      }

      console.info('[BatchAdCreate] retry start', {
        batchId: requestData.client_batch_id,
        index: requestData.client_batch_index,
        adName: requestData.ad_name
      })

      try {
        const res = await adApi.create(requestData)
        const durationMs = Date.now() - retryStartTime
        if (res.code === 0) {
          this.$message.success('重试成功')
          this.submitResults.successList.push({
            adName: requestData.ad_name,
            adId: res.data?.smart_plus_ad_id || '未知',
            adgroupName: requestData.adgroup_name,
            durationMs,
            durationText: this.formatDuration(durationMs)
          })
          const index = this.submitResults.failList.indexOf(failItem)
          if (index > -1) {
            this.submitResults.failList.splice(index, 1)
          }
          console.info('[BatchAdCreate] retry success', {
            batchId: requestData.client_batch_id,
            index: requestData.client_batch_index,
            adId: res.data?.smart_plus_ad_id,
            durationMs
          })
        } else {
          this.$message.error('重试失败: ' + res.message)
          console.warn('[BatchAdCreate] retry fail', {
            batchId: requestData.client_batch_id,
            index: requestData.client_batch_index,
            message: res.message,
            durationMs
          })
        }
      } catch (error) {
        const durationMs = Date.now() - retryStartTime
        this.$message.error('重试失败: ' + error.message)
        console.error('[BatchAdCreate] retry error', {
          batchId: requestData.client_batch_id,
          index: requestData.client_batch_index,
          message: error.message,
          durationMs
        }, error)
      }
    },

    async retryAllFailedAds() {
      const retryableItems = this.submitResults.failList.filter(item => item.canRetry)
      for (const item of retryableItems) {
        await this.retryCreateAd(item)
      }
    },

    openAdCreateLogPage() {
      if (!this.submitResults.batchId) return
      this.submitResultDialogVisible = false
      this.$router.push({
        path: '/tiktok/ad-create-logs',
        query: { batchId: this.submitResults.batchId }
      })
    },

    resetAll() {
      this.$confirm('确定要重置所有配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.selectedAdvertiserIds = []
        this.advertiserConfigs = []
        this.globalVideoLibrary = []
        this.adConfig = {
          adNamePrefix: '',
          adTexts: [{ content: '' }],
          selectedCtas: [],
          landingPageUrl: '',
          operationStatus: 'ENABLE',
          videosPerAd: 1
        }
        localStorage.removeItem(this.VIDEO_LIBRARY_STORAGE_KEY)
        this.$message.success('已重置')
      })
    },

    formatDateTime(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    addHoursToDate(dateString, hours) {
      if (!dateString) return dateString
      const date = new Date(dateString)
      date.setHours(date.getHours() + hours)
      return this.formatDateTime(date)
    },

    // ========== 配置管理方法 ==========
    handleConfigCommand(command) {
      if (command === 'save') {
        this.openSaveConfigDialog()
      } else if (command === 'load') {
        this.openLoadConfigDialog()
      }
    },

    openSaveConfigDialog() {
      this.saveConfigFormData = { name: '', description: '' }
      this.saveConfigDialogVisible = true
    },

    handleSaveConfigDialogClose() {
      this.$refs.saveConfigForm && this.$refs.saveConfigForm.resetFields()
    },

    async handleSaveConfig() {
      try {
        await this.$refs.saveConfigForm.validate()
        this.saveConfigLoading = true

        // 构建配置对象（不包含视频素材和广告主选择）
        const configData = {
          adConfig: {
            adNamePrefix: this.adConfig.adNamePrefix,
            adTexts: JSON.parse(JSON.stringify(this.adConfig.adTexts)),
            selectedCtas: [...this.adConfig.selectedCtas],
            landingPageUrl: this.adConfig.landingPageUrl,
            operationStatus: this.adConfig.operationStatus,
            videosPerAd: this.adConfig.videosPerAd
          },
          createAdgroupFormData: {
            objective_type: this.createAdgroupFormData.objective_type,
            budget_mode: this.createAdgroupFormData.budget_mode,
            budget: this.createAdgroupFormData.budget,
            promotion_type: this.createAdgroupFormData.promotion_type,
            minis_id: this.createAdgroupFormData.minis_id,
            optimization_goal: this.createAdgroupFormData.optimization_goal,
            bid_type: this.createAdgroupFormData.bid_type,
            conversion_bid_price: this.createAdgroupFormData.conversion_bid_price,
            bid_price: this.createAdgroupFormData.bid_price,
            deep_bid_type: this.createAdgroupFormData.deep_bid_type,
            roas_bid: this.createAdgroupFormData.roas_bid,
            schedule_type: this.createAdgroupFormData.schedule_type,
            schedule_start_time: this.createAdgroupFormData.schedule_start_time,
            schedule_end_time: this.createAdgroupFormData.schedule_end_time,
            placement_type: this.createAdgroupFormData.placement_type,
            placements: [...this.createAdgroupFormData.placements],
            targeting_location_ids: [...this.createAdgroupFormData.targeting_location_ids],
            targeting_languages: [...this.createAdgroupFormData.targeting_languages]
          }
        }

        // 加载现有配置列表
        const configs = this.loadConfigsFromStorage()

        // 添加新配置
        const newConfig = {
          id: Date.now().toString(),
          name: this.saveConfigFormData.name,
          description: this.saveConfigFormData.description,
          createTime: this.formatDateTime(new Date()),
          data: configData
        }

        configs.unshift(newConfig)

        // 保存到 localStorage
        localStorage.setItem(this.CONFIG_STORAGE_KEY, JSON.stringify(configs))

        this.$message.success('配置保存成功')
        this.saveConfigDialogVisible = false
        this.savedConfigs = configs
      } catch (error) {
        console.error('保存配置失败', error)
        this.$message.error('保存配置失败')
      } finally {
        this.saveConfigLoading = false
      }
    },

    loadConfigsFromStorage() {
      try {
        const cached = localStorage.getItem(this.CONFIG_STORAGE_KEY)
        if (cached) {
          const parsed = JSON.parse(cached)
          if (Array.isArray(parsed)) {
            return parsed
          }
        }
      } catch (error) {
        console.error('加载配置列表失败', error)
      }
      return []
    },

    openLoadConfigDialog() {
      this.savedConfigs = this.loadConfigsFromStorage()
      this.loadConfigDialogVisible = true
    },

    handleLoadConfig(config) {
      this.$confirm(`确定要加载配置 "${config.name}" 吗？当前未保存的配置将会丢失。`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 应用广告配置
        if (config.data.adConfig) {
          this.adConfig = {
            ...this.adConfig,
            ...config.data.adConfig
          }
        }

        // 应用广告组创建配置
        if (config.data.createAdgroupFormData) {
          this.createAdgroupFormData = {
            ...this.createAdgroupFormData,
            ...config.data.createAdgroupFormData
          }
        }

        this.loadConfigDialogVisible = false
        this.$message.success('配置加载成功')
      })
    },

    handleDuplicateConfig(config) {
      this.saveConfigFormData = {
        name: config.name + ' - 副本',
        description: config.description
      }
      this.saveConfigDialogVisible = true
      this.loadConfigDialogVisible = false
    },

    handleDeleteConfig(index) {
      this.$confirm('确定要删除该配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.savedConfigs.splice(index, 1)
        localStorage.setItem(this.CONFIG_STORAGE_KEY, JSON.stringify(this.savedConfigs))
        this.$message.success('配置已删除')
      })
    },

    // 打印内存使用情况
    logMemoryUsage() {
      if (performance.memory) {
        const memory = performance.memory
        const usedMB = (memory.usedJSHeapSize / 1048576).toFixed(2)
        const totalMB = (memory.totalJSHeapSize / 1048576).toFixed(2)
        const limitMB = (memory.jsHeapSizeLimit / 1048576).toFixed(2)
        const usagePercent = ((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100).toFixed(2)
        
        console.log('=== 内存使用情况 ===')
        console.log(`已使用: ${usedMB} MB`)
        console.log(`总分配: ${totalMB} MB`)
        console.log(`上限: ${limitMB} MB`)
        console.log(`使用率: ${usagePercent}%`)
        console.log(`视频数量: ${this.globalVideoLibrary.length}`)
        console.log(`选中视频: ${this.selectedVideos.length}`)
        console.log(`DOM节点数: ${document.querySelectorAll('*').length}`)
        console.log('====================')
      } else {
        console.warn('当前浏览器不支持 performance.memory API，请使用 Chrome 浏览器')
      }
    }
  }
}
</script>

<style scoped>
.batch-ad-creator {
  padding: 16px;
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.page-header {
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 500;
  color: #1f2f3d;
}

.page-desc {
  margin: 0;
  font-size: 13px;
  color: #909399;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.submit-progress-card {
  margin-bottom: 16px;
  border-radius: 4px;
}

.submit-progress-card >>> .el-card__body {
  padding: 14px 16px;
}

.submit-progress-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.submit-progress-title {
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.submit-progress-meta {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}

.submit-progress-count {
  color: #606266;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.submit-result-meta {
  margin: 0 0 12px;
}

/* 上半部分：三栏配置 - 高度自适应 */
.config-layout {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
  align-items: start;
}

.config-card {
  height: auto;
  display: flex;
  flex-direction: column;
}

.config-card >>> .el-card__body {
  padding: 16px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 15px;
  font-weight: 500;
}

/* 视频素材样式 - 9:16 竖屏比例 */
.materials-stats {
  margin-bottom: 12px;
  display: flex;
  gap: 8px;
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
  max-height: 540px; /* 约3行视频的高度 */
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px;
  position: relative;
  scrollbar-width: thin;
  scrollbar-color: #c0c4cc #f5f7fa;
}

.video-grid::-webkit-scrollbar {
  width: 8px;
}

.video-grid::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 4px;
}

.video-grid::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

.video-grid::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

.video-item {
  position: relative;
  border: 2px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s;
  background: #fff;
}

.video-item:hover {
  border-color: #409eff;
}

.video-item.selected {
  border-color: #67c23a;
  box-shadow: 0 2px 8px rgba(103, 194, 58, 0.3);
}

.video-checkbox {
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 10;
}

/* 9:16 竖屏比例 - 720x1280 */
.video-preview-wrapper {
  width: 100%;
  aspect-ratio: 9 / 16;
  background: #000;
  position: relative;
  overflow: hidden;
}

.video-preview-wrapper video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-preview-wrapper img.video-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.video-info {
  padding: 6px 8px;
  background: #f5f7fa;
}

.video-index {
  font-size: 10px;
  color: #909399;
  display: block;
}

.video-name {
  font-size: 11px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.video-review-line {
  margin-top: 4px;
  min-height: 20px;
}

.batch-actions {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e4e7ed;
  display: flex;
  gap: 8px;
}

/* 广告主配置样式 */
.config-section {
  margin-bottom: 16px;
}

.section-title {
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 8px;
}

.required {
  color: #f56c6c;
}

.advertiser-config-block {
  background: #f5f7fa;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}

.adv-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.adv-name {
  font-weight: 500;
  color: #303133;
}

.section-subtitle {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.ad-count-config {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ad-count-config .label {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

/* 广告配置样式 */
.config-item {
  margin-bottom: 16px;
}

.config-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
}

.form-tip {
  font-size: 11px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

.ad-text-list {
  width: 100%;
}

.ad-text-item {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

/* 下半部分：全宽预览 */
.preview-section {
  margin-top: 0;
  overflow: visible;
}

.preview-section >>> .el-card {
  overflow: visible;
}

.preview-section >>> .el-card__body {
  padding: 16px;
  overflow: visible;
}

.preview-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.stat-box {
  text-align: center;
  padding: 16px 24px;
  background: #f5f7fa;
  border-radius: 8px;
  min-width: 100px;
}

.stat-box.highlight {
  background: #ecf5ff;
}

.stat-box.highlight .stat-value {
  color: #409eff;
}

.stat-box.info {
  background: #f0f9eb;
}

.stat-box.info .stat-value {
  color: #67c23a;
}

.stat-value {
  font-size: 28px;
  font-weight: 600;
  color: #606266;
  line-height: 1;
  margin-bottom: 6px;
}

.stat-label {
  font-size: 13px;
  color: #606266;
}

.preview-title {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.distribution-preview {
  width: 100%;
}

.distribution-preview >>> .el-table {
  max-height: 400px;
  overflow-y: auto;
}

.video-names-cell {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 250px;
}

/* 视频预览单元格样式 */
.video-preview-cell {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* 横向滚动缩略图 */
.video-thumbnails-scroll {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  max-width: 200px;
  padding: 4px 0;
  scrollbar-width: thin;
  scrollbar-color: #c0c4cc #f5f7fa;
}

.video-thumbnails-scroll::-webkit-scrollbar {
  height: 6px;
}

.video-thumbnails-scroll::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 3px;
}

.video-thumbnails-scroll::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 3px;
}

.video-thumbnails-scroll::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

.thumbnail-scroll-item {
  position: relative;
  width: 50px;
  height: 70px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  background: #000;
  border: 1px solid #e4e7ed;
}

.thumbnail-scroll-item video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-scroll-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.thumb-index {
  position: absolute;
  top: 2px;
  left: 2px;
  background: rgba(64, 158, 255, 0.9);
  color: #fff;
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 2px;
  font-weight: bold;
  line-height: 1;
}

.video-info-cell {
  flex: 1;
  min-width: 0;
}

/* 视频预览弹窗样式 */
.video-preview-dialog {
  max-height: 600px;
  overflow-y: auto;
}

.preview-ad-info {
  margin-bottom: 20px;
}

.preview-video-list {
  margin-top: 16px;
}

.video-grid-preview {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.video-preview-item {
  position: relative;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.video-index-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(64, 158, 255, 0.9);
  color: #fff;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  z-index: 10;
  font-weight: bold;
}

.video-preview-item video {
  width: 100%;
  aspect-ratio: 9 / 16;
  background: #000;
  display: block;
}

.video-preview-item img {
  width: 100%;
  aspect-ratio: 9 / 16;
  background: #000;
  display: block;
  object-fit: contain;
}

.video-filename {
  padding: 8px;
  font-size: 12px;
  color: #606266;
  background: #f5f7fa;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-tip {
  font-size: 12px;
  color: #909399;
  text-align: center;
  margin-top: 8px;
}

/* 抽屉样式 */
.drawer-content {
  padding: 0px;
  height: 100%;
}

/* 响应式 */
@media (max-width: 1400px) {
  .config-layout {
    grid-template-columns: 1fr 1fr;
  }
  .right-panel {
    grid-column: span 2;
  }
}

@media (max-width: 900px) {
  .config-layout {
    grid-template-columns: 1fr;
  }
  .right-panel {
    grid-column: span 1;
  }
  .video-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
